<template>
  <div class="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2 font-sans">
    <!-- Main Widget Window -->
    <transition name="slide-up">
      <div v-if="isOpen" class="bg-white rounded-xl shadow-2xl w-[90vw] md:w-[400px] h-[80vh] md:h-[600px] border border-gray-200 flex flex-col overflow-hidden">

        <!-- Header -->
        <div class="bg-primary text-white p-3 flex justify-between items-center shadow-sm z-10">
            <div class="flex items-center gap-2 overflow-hidden">
                 <Button
                    v-if="currentView !== 'list'"
                    icon="pi pi-arrow-left"
                    text rounded
                    class="text-white !p-0 !w-8 !h-8 hover:bg-primary/90"
                    data-testid="btn-back"
                    @click="goBack"
                 />
                 <h3 class="font-bold truncate text-base">
                    {{ headerTitle }}
                 </h3>
            </div>
            <Button
                icon="pi pi-times"
                text
                rounded
                class="!p-0 !w-8 !h-8 !text-white !bg-transparent !shadow-none hover:!bg-white/10"
                @click="closeWidget"
                data-testid="btn-close-widget"
            />
        </div>

        <!-- VIEW: LIST -->
        <div v-if="currentView === 'list'" class="flex-1 flex flex-col overflow-hidden">
            <!-- Tabs -->
            <div class="flex border-b border-gray-200">
                <button
                    @click="activeListTab = 'active'"
                    data-testid="btn-tab-active-tickets"
                    class="flex-1 py-3 text-sm font-semibold transition-colors relative"
                    :class="activeListTab === 'active' ? 'text-primary' : 'text-gray-500 hover:text-gray-700'"
                >
                    Active
                    <div v-if="activeListTab === 'active'" class="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></div>
                </button>
                <button
                    @click="activeListTab = 'closed'"
                    data-testid="btn-tab-closed-tickets"
                    class="flex-1 py-3 text-sm font-semibold transition-colors relative"
                    :class="activeListTab === 'closed' ? 'text-primary' : 'text-gray-500 hover:text-gray-700'"
                >
                    Solved & Closed
                     <div v-if="activeListTab === 'closed'" class="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></div>
                </button>
            </div>

            <!-- Ticket List -->
            <div class="flex-1 overflow-y-auto p-2 bg-gray-50">
                <div v-if="loadingList" class="flex justify-center p-4">
                    <i class="pi pi-spin pi-spinner text-primary"></i>
                </div>

                <div v-else-if="currentList.length === 0" class="flex flex-col items-center justify-center h-full text-gray-400 p-4 text-center">
                    <i class="pi pi-inbox text-4xl mb-2 opacity-50"></i>
                    <p class="text-sm">No {{ activeListTab }} tickets.</p>
                </div>

                <div v-else class="flex flex-col gap-2">
                    <div
                        v-for="ticket in currentList"
                        :key="ticket.id"
                        class="bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer active:scale-[0.99]"
                        @click="openChat(ticket.id)"
                    >
                        <div class="flex justify-between items-start mb-1">
                            <span class="font-semibold text-gray-800 text-sm line-clamp-1">{{ ticket.subject }}</span>
                            <span class="text-[10px] text-gray-400 whitespace-nowrap ml-2">{{ new Date(ticket.created_at).toLocaleDateString() }}</span>
                        </div>
                        <div class="flex justify-between items-center text-xs">
                             <span class="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{{ ticket.category?.name }}</span>
                             <TicketStatusBadge :status="ticket.status" size="small" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- New Ticket FAB (Internal) -->
            <div class="absolute bottom-4 right-4">
                <Button
                    icon="pi pi-plus"
                    rounded
                    raised
                    class="!w-12 !h-12 !bg-primary border-none shadow-lg"
                    data-testid="btn-new-ticket"
                    @click="currentView = 'create'"
                />
            </div>
        </div>

        <!-- VIEW: CREATE -->
        <div v-else-if="currentView === 'create'" class="flex-1 overflow-y-auto p-4 flex flex-col gap-4 bg-gray-50">
             <div class="bg-white p-3 rounded-lg shadow-sm">
                <!-- Category -->
                 <div class="flex flex-col gap-1 mb-3">
                    <label class="text-xs font-semibold text-gray-500 uppercase">Category <span class="text-red-500">*</span></label>
                    <Select
                        v-model="createForm.ticket_category_id"
                        :options="categories"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Select a topic"
                        class="w-full p-inputtext-sm"
                        :loading="loadingCategories"
                    />
                 </div>

                 <!-- Subject -->
                 <div class="flex flex-col gap-1 mb-3">
                    <label class="text-xs font-semibold text-gray-500 uppercase">Subject <span class="text-red-500">*</span></label>
                    <InputText v-model="createForm.subject" placeholder="Brief summary" class="w-full p-inputtext-sm" />
                 </div>

                 <!-- Description -->
                 <div class="flex flex-col gap-1">
                    <label class="text-xs font-semibold text-gray-500 uppercase">Description <span class="text-red-500">*</span></label>
                    <Textarea v-model="createForm.description" rows="4" placeholder="How can we help?" class="w-full text-sm" />
                 </div>
             </div>

             <!-- Attachments -->
             <div class="bg-white p-3 rounded-lg shadow-sm">
                <label class="text-xs font-semibold text-gray-500 uppercase mb-2 block">Attachments</label>
                <div class="flex flex-col gap-3">
                     <div class="flex gap-2">
                         <Button
                            :icon="showRecorder ? 'pi pi-times' : 'pi pi-microphone'"
                            :label="showRecorder ? 'Cancel' : 'Record Voice'"
                            :severity="showRecorder ? 'secondary' : 'help'"
                            size="small"
                            outlined
                            class="flex-1"
                            data-testid="btn-record-voice"
                            @click="toggleRecorder"
                         />
                    </div>
                    <div v-if="showRecorder" class="bg-gray-50 p-2 rounded border border-gray-200">
                        <AudioRecorder @recorded="handleVoiceRecorded" />
                    </div>
                    <FileUploader @uploaded="handleUploads" :maxFiles="3" />
                </div>
             </div>

             <div class="mt-auto">
                 <Button label="Submit Ticket" icon="pi pi-send" class="w-full" @click="onSubmitTicket" :loading="creatingTicket" data-testid="btn-submit-ticket" />
             </div>
        </div>

        <!-- VIEW: CHAT -->
        <div v-else-if="currentView === 'chat'" class="flex-1 flex flex-col overflow-hidden bg-[#eef1f6]">
            <!-- Messages Area -->
            <div class="flex-1 overflow-y-auto p-3 flex flex-col gap-2" ref="chatContainer">
                <div v-if="loadingChat" class="flex justify-center p-4">
                     <i class="pi pi-spin pi-spinner text-primary"></i>
                </div>
                <div v-else-if="currentMessages.length === 0" class="text-center text-gray-400 text-xs py-4">
                    No messages yet.
                </div>
                <div
                    v-for="msg in currentMessages"
                    :key="msg.id"
                    class="max-w-[85%] rounded-2xl px-4 py-2 text-sm relative group shadow-sm"
                    :class="isOwnMessage(msg) ? 'bg-primary text-white self-end rounded-br-none' : 'bg-white text-gray-800 self-start rounded-bl-none border border-gray-200'"
                >
                    <div class="font-bold text-[10px] mb-0.5 opacity-80" :class="isOwnMessage(msg) ? 'text-primary-100' : 'text-gray-500'">
                        {{ getSenderName(msg.sender) }}
                    </div>
                    <p class="whitespace-pre-wrap leading-relaxed">{{ msg.message }}</p>

                     <!-- Attachments -->
                    <div v-if="msg.attachments && msg.attachments.length" class="mt-2 pt-2 border-t" :class="isOwnMessage(msg) ? 'border-white/20' : 'border-gray-100'">
                        <div class="flex flex-wrap gap-2">
                             <template v-for="att in msg.attachments" :key="att.id">
                                <template v-if="att.file.mime_type?.startsWith('image/')">
                                     <Image :src="att.file.url || ''" alt="Image" width="80" preview class="rounded overflow-hidden" />
                                </template>
                                <template v-else-if="att.file.mime_type?.startsWith('audio/')">
                                      <audio controls :src="att.file.url || ''" class="h-6 w-[150px]"></audio>
                                </template>
                                 <a
                                    v-else
                                    :href="att.file.url || '#'"
                                    target="_blank"
                                    class="flex items-center gap-1 hover:underline text-xs"
                                    :class="isOwnMessage(msg) ? 'text-white' : 'text-primary'"
                                >
                                    <i class="pi pi-paperclip"></i>
                                    {{ att.file.original_name }}
                                </a>
                             </template>
                        </div>
                    </div>

                    <div class="text-[10px] text-right mt-1 opacity-70">
                         {{ new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) }}
                    </div>
                </div>
            </div>

            <!-- Input Area -->
            <div class="bg-white p-2 border-t border-gray-200 flex flex-col gap-2">
                 <div class="flex gap-2 items-end">
                    <Textarea
                        v-model="replyMessage"
                        autoResize
                        rows="1"
                        class="flex-1 !min-h-[40px] !max-h-[100px] text-sm"
                        placeholder="Type a message..."
                        @keydown.enter.prevent="submitReply"
                    />
                    <Button
                        icon="pi pi-send"
                        rounded
                        text
                        class="!w-10 !h-10 text-primary hover:bg-primary/10"
                        data-testid="btn-send-reply"
                        @click="submitReply"
                        :loading="replyingTicket"
                        :disabled="!replyMessage.trim() && !replyFileIds.length && !audioFileId"
                    />
                 </div>

                 <div class="flex justify-between items-center px-1">
                     <div class="flex gap-2">
                        <Button icon="pi pi-paperclip" text rounded size="small" class="text-gray-500" @click="showReplyUploads = !showReplyUploads" data-testid="btn-show-uploads" />
                        <Button :icon="showReplyRecorder ? 'pi pi-times' : 'pi pi-microphone'" text rounded size="small" :class="showReplyRecorder ? 'text-red-500' : 'text-gray-500'" @click="showReplyRecorder = !showReplyRecorder" data-testid="btn-show-recorder" />
                     </div>
                 </div>

                 <!-- Collapsible Upload/Record areas -->
                  <div v-if="showReplyRecorder" class="bg-gray-50 p-2 rounded">
                        <AudioRecorder @recorded="(id) => audioFileId = id" />
                  </div>
                  <div v-if="showReplyUploads" class="bg-gray-50 p-2 rounded">
                        <FileUploader @uploaded="(ids) => replyFileIds = ids" :maxFiles="3" />
                  </div>
            </div>
        </div>

      </div>
    </transition>

    <!-- Floating Action Button -->
    <div v-if="!isOpen" class="relative">
        <Button
            icon="pi pi-comments"
            rounded
            size="large"
            class="w-14 h-14 shadow-xl text-xl !bg-primary hover:!bg-primary/90 !border-none animate-bounce-slow"
            data-testid="btn-open-widget"
            @click="openWidget"
            v-tooltip.left="'Support Chat'"
        />
        <!-- Badge -->
        <span
            v-if="notifStore.unreadTicketCount > 0"
            class="absolute top-0 right-0 w-4 h-4 bg-red-500 border-2 border-white rounded-full flex items-center justify-center"
        >
        </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTicketQueries } from '@/composables/queries/useTicketQueries';
import { ON_TICKET_MESSAGE_SENT } from '@/graphql/subscriptions/tickets';
import { useTicketMutations } from '@/composables/mutations/useTicketMutations';
import { useUserStore } from '@/store/userStore';
import type { Ticket, TicketMessage, UserOrStudent } from '@/types/composition-functions';
import { useNotificationStore } from '@/store/notificationStore'; // Import Store
import { useSubscription } from '@vue/apollo-composable';
import TicketStatusBadge from '@/components/common/TicketStatusBadge.vue';
import FileUploader from '@/components/common/FileUploader.vue';
import AudioRecorder from '@/components/common/AudioRecorder.vue';
import { useToast } from 'primevue/usetoast';

// Types
type ViewType = 'list' | 'create' | 'chat';
type ListTab = 'active' | 'closed';

// State
const isOpen = ref(false);
const currentView = ref<ViewType>('list');
const activeListTab = ref<ListTab>('active');
const selectedTicketId = ref<string | null>(null);

const notifStore = useNotificationStore(); // Init Store

// Create Form State
const showRecorder = ref(false);
const createForm = reactive({
  subject: '',
  description: '',
  ticket_category_id: '',
  file_ids: [] as string[],
});

// Chat State
const replyMessage = ref('');
const showReplyUploads = ref(false);
const showReplyRecorder = ref(false);
const replyFileIds = ref<string[]>([]);
const audioFileId = ref<string | null>(null);
const chatContainer = ref<HTMLElement | null>(null);

// Composables
const { useGetCategories, useGetTickets, useGetTicket } = useTicketQueries();
const { createTicket, creatingTicket, replyTicket, replyingTicket } = useTicketMutations();
const toast = useToast();
const userStore = useUserStore();
const user = computed(() => userStore.currentUser);

// Data
const { categories, loading: loadingCategories } = useGetCategories();
const { tickets, loading: loadingList, refetch: refetchList } = useGetTickets(); // Fetch ALL tickets, filter client side

// Computed Lists
const currentList = computed(() => {
  if (!tickets.value) return [];
  if (activeListTab.value === 'active') {
    return tickets.value.filter((t) => t.status !== 'CLOSED' && t.status !== 'RESOLVED');
  } else {
    return tickets.value.filter((t) => t.status === 'CLOSED' || t.status === 'RESOLVED');
  }
});

// Single Ticket Data (Lazy loaded when ID is set)
const ticketData = ref<Ticket | null>(null);
const currentMessages = ref<TicketMessage[]>([]);

import { GET_TICKET } from '@/graphql/Queries/tickets';
import { useQuery } from '@vue/apollo-composable';

import { extractGraphQLError } from '@/utils/graphqlError';
const { result: ticketResult, loading: loadingChat, refetch: refetchChat, onResult: onTicketResult } = useQuery(GET_TICKET, () => ({
  id: selectedTicketId.value,
}), {
  enabled: computed(() => !!selectedTicketId.value),
  fetchPolicy: 'network-only',
});

watch(ticketResult, (newVal) => {
  if(newVal?.ticket) {
    ticketData.value = newVal.ticket;
    currentMessages.value = [...(newVal.ticket.messages || [])];
    scrollToBottom();
  }
});

// Subscription
// We need to subscribe when a ticket is selected.
// `useSubscription` setup.
const { onResult: onMessageSent } = useSubscription(ON_TICKET_MESSAGE_SENT, () => ({
  ticket_id: selectedTicketId.value,
}), {
  enabled: computed(() => !!selectedTicketId.value),
});

onMessageSent((result) => {
  if (result.data?.ticketMessageSent && selectedTicketId.value) {
    // Add to messages
    const exists = currentMessages.value.some((m) => m.id === result.data.ticketMessageSent.id);
    if (!exists) {
      currentMessages.value.push(result.data.ticketMessageSent);
      scrollToBottom();
    }
  }
});

const headerTitle = computed(() => {
  if (currentView.value === 'list') return 'Support Tickets';
  if (currentView.value === 'create') return 'New Ticket';
  if (currentView.value === 'chat') return ticketData.value?.subject || 'Chat';
  return 'Support';
});

// Actions
const openWidget = () => {
  isOpen.value = true;
  notifStore.clearTicketBadge(); // Clear badge on open
  refetchList();
};

const closeWidget = () => {
  isOpen.value = false;
};

const goBack = () => {
  if (currentView.value === 'create' || currentView.value === 'chat') {
    currentView.value = 'list';
    selectedTicketId.value = null;
    createForm.subject = ''; // Clear create form? Maybe keep if filtered.
    refetchList();
  }
};

const openChat = (id: string) => {
  selectedTicketId.value = id;
  currentView.value = 'chat';
  // Chat query automatically enables
};

// Create Logic
const handleUploads = (ids: string[]) => {
  const current = new Set([...createForm.file_ids, ...ids]);
  createForm.file_ids = Array.from(current);
};

const handleVoiceRecorded = (fileId: string | null) => {
  if(fileId) createForm.file_ids.push(fileId);
};

const toggleRecorder = () => {
  showRecorder.value = !showRecorder.value;
};

const onSubmitTicket = async() => {
  if (!createForm.subject || !createForm.ticket_category_id || !createForm.description.trim()) {
    toast.add({ severity: 'error', summary: 'Required', detail: 'Please fill in Subject, Category and Description.', life: 3000 });
    return;
  }

  try {
    const res = await createTicket({
      input: {
        subject: createForm.subject,
        description: createForm.description,
        ticket_category_id: createForm.ticket_category_id,
        // Priority optional/removed
        file_ids: createForm.file_ids,
      },
    });

    toast.add({ severity: 'success', summary: 'Ticket Sent', detail: 'Ticket created successfully.', life: 3000 });

    // Auto open the new ticket
    const newId = res?.data?.createTicket?.id;
    if (newId) {
      openChat(newId);
    } else {
      currentView.value = 'list';
      refetchList();
    }

    // Reset Form
    createForm.subject = '';
    createForm.description = '';
    createForm.ticket_category_id = '';
    createForm.file_ids = [];
    showRecorder.value = false;

  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(error, 'Failed to create ticket.'), life: 3000 });
  }
};

// Watch for external open triggers
watch(() => notifStore.widgetTrigger, () => {
  openWidget();
});

// Chat Logic
const isOwnMessage = (msg: TicketMessage) => {
  const currentUser = user.value;
  if (!currentUser) return false;

  const sender = msg.sender;
  if (!sender) return false;

  // Determine current user type based on fields (Student has matric_no/surname, User has name)
  // Safe heuristic: If I have 'matric_no' I am a Student.
  const amIStudent = 'matric_no' in currentUser || 'surname' in currentUser;

  // Determine sender type based on GraphQL fragment
  // User has { id, name }
  // Student has { id, surname, first_name }
  const senderIsStudent = 'surname' in sender || 'first_name' in sender;

  if (amIStudent) {
    // I am Student. Own message if sender is Student AND IDs match.
    return senderIsStudent && sender.id === currentUser.id;
  } else {
    // I am Admin/User. Own message if sender is User AND IDs match.
    return !senderIsStudent && sender.id === currentUser.id;
  }
};

const getSenderName = (sender: UserOrStudent) => {
  if (!sender) return 'Unknown';
  if (sender.name) return sender.name; // User
  if (sender.first_name) return `${sender.first_name} ${sender.surname || ''}`; // Student
  return 'User';
};

const submitReply = async() => {
  const hasContent = replyMessage.value.trim() || replyFileIds.value.length > 0 || audioFileId.value;
  if (!hasContent) return;

  const finalFileIds = [...replyFileIds.value];
  if (audioFileId.value) finalFileIds.push(audioFileId.value);

  try {
    await replyTicket({
      input: {
        ticket_id: selectedTicketId.value!,
        message: replyMessage.value.trim() || ' ',
        file_ids: finalFileIds,
      },
    });
    // Optimistic UI or wait for subscription?
    // Subscription will add it.
    replyMessage.value = '';
    replyFileIds.value = [];
    audioFileId.value = null;
    showReplyUploads.value = false;
    showReplyRecorder.value = false;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(e, 'Failed to send reply.'), life: 3000 });
  }
};

const scrollToBottom = () => {
  nextTick(() => {
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
    }
  });
};

</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.animate-bounce-slow {
    animation: bounce 3s infinite;
}
</style>
